<template>
  <section
    ref="slideShowWrapper"
    class="slideshow"
    :class="isARIndicatorSelected && 'ar-indicator-main-image-wrapper'"
    tabindex="0"
    aria-label="Product image slideshow"
    data-test="main_image_container"
    :data-orientation-id="selectedType?.id"
    :data-orientation-name="selectedType?.name"
    :aria-labelledby="`product-slideshow slide-${currentPage}`"
  >
    <div class="focus-border"></div>
    <component
      :is="isMobile && !inOverlay ? SlideshowNew : Slideshow"
      v-if="!isTopLevelZoomerClick || inOverlay || isMobile"
      ref="slideShow"
      :list="allImages"
      :is-zoomed-in="isZoomedIn"
      :initial-index="currentPage"
      :first-alt-dot="firstAltDot"
      :autoplay-mode="'manual'"
      :is-autoplayed="!isMainImageInteracted"
      :autoplay-interval="3000"
      :in-overlay="inOverlay"
      :show-indicator="showIndicator"
      :threshold-limit="0.8"
      @slideshow-change="slideShowChangeHandler"
      @update-first-alt-dot="updateFirstAltDotHandler"
    >
      <!-- eslint-disable jsx-a11y/click-events-have-key-events -->
      <template #item="slotProps">
        <div v-if="slotProps.data.video" class="product-video-wrapper">
          <NewProductVideo v-if="inHouseVideoEnabled" :video-id="videoId" :is-video-selected="isVideoSelected" />
          <ProductVideo v-else :video-id="videoId" :is-video-selected="isVideoSelected" />
        </div>
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <div
          v-else-if="slotProps.data.isARIndicatorExp"
          class="ar-indicator-wrapper grid-x"
          @touchstart="handleVTOTouch"
          @touchmove="handleVTOTouch"
          @mousedown="handleVTOTouch"
        >
          <component :is="slotProps.data.component" v-bind="slotProps.data.props" />
        </div>
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
        <div v-else class="product-image-wrapper mobile" @click="openOverlay">
          <div class="image-layer"></div>
          <PictureView
            :image-url="allImages[slotProps.index].path"
            :alt="imageAlt"
            :image-sizes-by-breakpoints="imageSizesByBreakpoints"
            :fetch-priority="getFetchPriority(slotProps.index)"
            :lazy-load="!getFetchPriority(slotProps.index)"
            :image-class="slotProps.index === 0 ? 'stylitics-shop-similar' : ''"
            :ai-image="isAiGeneratedImages"
            :ai-image-overrides
            draggable="false"
          />
          <transition name="fade">
            <JumboSwatch v-if="allImages[slotProps.index].showJumboSwatch && !inOverlay && !showImageUnavailable" />
          </transition>
          <ImageSwatch
            v-if="allImages[slotProps.index].showJumboSwatch && !inOverlay && showImageUnavailable"
            :is-zoomed-in="isZoomedIn"
            :selected-color="selectedColor"
            :toast-container-id="'slideshow-improved-container-' + slotProps.index"
          />
        </div>
      </template>
      <template #zoom-icon>
        <button
          v-if="!isMobile && !inOverlay && !isVideoSelected && !isImageIndicatorStylingEnabled"
          data-test-id="fullscreen-entry"
          aria-label="View product image in full screen"
          class="overlay-entry full-screen-dark-medium"
          @click="openOverlay"
          @keydown.space.prevent="openOverlay"
        ></button>
      </template>
    </component>
    <div v-else class="zoomer-top-level-container">
      <div :class="{ 'zoomed-in': isZoomedIn }">
        <div class="main-image-zoom-background"></div>
        <ImageZoomer class="images-slideshow top-level-zoom" :current-page="currentPage" :top-level-applied="true">
          <Slideshow
            ref="slideShow"
            :list="allImages"
            :is-zoomed-in="isZoomedIn"
            :initial-index="currentPage"
            :first-alt-dot="firstAltDot"
            :autoplay-mode="'manual'"
            :is-autoplayed="!isMainImageInteracted"
            :autoplay-interval="3000"
            :in-overlay="true"
            :show-indicator="showIndicator"
            :threshold-limit="0.8"
            :top-level-zoomer-mobile="topLevelZoomerMobile"
            @slideshow-change="slideShowChangeHandler"
            @update-first-alt-dot="updateFirstAltDotHandler"
          >
            <template #item="slotProps">
              <div v-if="slotProps.data.video" class="product-video-wrapper">
                <NewProductVideo v-if="inHouseVideoEnabled" :video-id="videoId" :is-video-selected="isVideoSelected" />
                <ProductVideo v-else :video-id="videoId" :is-video-selected="isVideoSelected" />
              </div>

              <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
              <div
                v-else-if="slotProps.data.isARIndicatorExp"
                class="ar-indicator-wrapper grid-x"
                @touchstart="handleVTOTouch"
                @touchmove="handleVTOTouch"
                @mousedown="handleVTOTouch"
              >
                <component :is="slotProps.data.component" v-bind="slotProps.data.props" />
              </div>
              <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
              <div v-else class="product-image-wrapper-top-level" @click="openOverlay">
                <div class="image-layer"></div>
                <PictureView
                  :image-url="allImages[slotProps.index].path"
                  :alt="imageAlt"
                  :image-sizes-by-breakpoints="imageSizesByBreakpoints"
                  :fetch-priority="getFetchPriority(slotProps.index)"
                  :lazy-load="!getFetchPriority(slotProps.index)"
                  :image-class="slotProps.index === 0 ? 'stylitics-shop-similar' : ''"
                  :ai-image="isAiGeneratedImages"
                  :ai-image-overrides
                  draggable="false"
                />
                <transition name="fade">
                  <JumboSwatch v-if="allImages[slotProps.index].showJumboSwatch && !inOverlay && !showImageUnavailable" />
                </transition>
                <ImageSwatch
                  v-if="allImages[slotProps.index].showJumboSwatch && !inOverlay && showImageUnavailable"
                  :toast-container-id="'slideshow-container-' + slotProps.index"
                  :is-zoomed-in="isZoomedIn"
                  :selected-color="selectedColor"
                />
              </div>
            </template>
            <template #zoom-icon>
              <button
                v-if="!isVideoSelected && !isImageIndicatorStylingEnabled"
                data-test-id="fullscreen-entry"
                aria-label="View product image in full screen"
                class="overlay-entry full-screen-dark-medium"
                @click="openOverlay"
                @keydown.space.prevent="openOverlay"
              ></button>
            </template>
          </Slideshow>
        </ImageZoomer>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useFocusWithin, onKeyStroke } from '@vueuse/core';
import { ref, computed, watch, onMounted, toValue, inject } from 'vue';
import { storeToRefs } from 'pinia';
import SlideshowNew from '~/components/common/Media/SlideShowImproved';
import ImageZoomer from '~/modules/image-zoomer/components/ImageZoomer.vue';
import { useType } from '~/modules/type/composables/useType';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useMarxentProvide } from '~/modules/marxent/composables/useMarxent';
import { useTopLevelCategoryFlags } from '~/modules/featureControl/composables/useTopLevelCategoryFlags';

import PictureView from '~/components/common/Media/PictureView';
import Slideshow from '~/components/common/Media/SlideShow';
import JumboSwatch from '~/components/JumboSwatch.vue';
import ImageSwatch from '~/components/ImageSwatch.vue';
import ProductVideo from '~/components/ProductVideo.vue';
import NewProductVideo from '~/components/NewProductVideo.vue';
import useConsolidatedProductDetails from '~/composables/useConsolidatedProductDetails';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useImages from '~/composables/images/useImages';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { useBrand } from '~/composables/useBrand';
import { useTraits } from '~/composables/useTraits';
import useOutfittingShopSimilar from '~/composables/useOutfittingShopSimilar';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useMainImageInteracted } from '~/composables/useMainImageInteracted';
import useExperiments from '~/stores/useExperimentsStore';
import { useMarxentIndicator } from '~/composables/useMarxentIndicator';
import { useImageryState } from '~/composables/useImageryState';

import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';

import once from '~/utils/once';
import { AI_WATERMARK_CONFIG } from '~/constants/common';

const { start } = useOutfittingShopSimilar();
const { isMainImageInteracted } = useMainImageInteracted();
const { isMarxentEligible } = useMarxentIndicator();

const imageSizesByBreakpoints = {
  small: 500,
  medium: 500,
  large: 500,
  mlarge: 700,
  xlarge: 700,
};

const props = defineProps({
  currentPage: {
    /** @type {import('vue').PropType<number>} */
    type: Number,
    default: 1,
  },
  firstAltDot: {
    /** @type {import('vue').PropType<number>} */
    type: Number,
    default: 1,
  },
  currentPageOnHover: {
    /** @type {import('vue').PropType<number | null>} */
    type: Number || null,
    default: null,
  },
  inOverlay: {
    /** @type {import('vue').PropType<boolean>} */
    type: Boolean,
    default: false,
  },
  showShopSimilar: {
    /** @type {import('vue').PropType<boolean>} */
    type: Boolean,
    default: false,
  },
  carouselImages: {
    /** @type {import('vue').PropType<Image[]>} */
    type: Array,
    require: true,
    default: () => [],
  },
  showIndicator: {
    /** @type {import('vue').PropType<boolean>} */
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['updateCarousel', 'imageClick', 'updateFirstAltDot']);

const triggerAltImageClickOnce = inject('triggerAltImageClickOnce', () => {});

const slideShow = ref(null);
const slideShowWrapper = ref(null);
const { productId } = useProduct();
const { selectedMember, memberFlags } = storeToRefs(useConsolidatedMemberStore());
const allowSwipe = ref(true);
const { focused } = useFocusWithin(slideShowWrapper);
const { isMobile } = useDeviceType();
const { selectedType } = useType();
const { isMcom, isBcom } = useBrand();
const { isHomeCategory } = useTopLevelCategoryFlags();
const { isChanelFlag, isExclusiveBrandFlag, isAiGeneratedMetaData } = useProductFlags();
const { videoId, videoIndex, indicatorIndex, vtoEligibleExperiment, pauseVideo } = useImages();
const { isTopLevelZoomerClick, isShopTheSetExpEnabled } = useFeatureEligibility();

const {
  experimentFlags: { isImageIndicatorStylingEnabled, isImageARIndicatorEnabled },
} = useExperiments();

const { isZoomedIn } = useImageryState();
/** @param {boolean} isZoom */

const {
  killswitches: { inHouseVideoEnabled },
} = useKillswitches();

const showImageUnavailable = isImageIndicatorStylingEnabled || isImageARIndicatorEnabled;
const isVideoSelected = computed(() => videoIndex.value === props.currentPageOnHover || videoIndex.value === props.currentPage - 1);
const isImageIndicatorExpEnabled = isImageARIndicatorEnabled && isImageIndicatorStylingEnabled;
const isARIndicatorSelected = computed(
  () => isImageIndicatorExpEnabled && (indicatorIndex.value === props.currentPageOnHover || indicatorIndex.value === props.currentPage - 1),
);

const isAiGeneratedImages = computed(() => !!memberFlags?.value?.isAiGeneratedMetaData || isAiGeneratedMetaData);
const aiImageOverrides = computed(() => {
  if (!isAiGeneratedImages.value) {
    return;
  }
  if (isMobile.value) {
    if (isBcom) {
      return isHomeCategory ? AI_WATERMARK_CONFIG.mobileBcomSquare : AI_WATERMARK_CONFIG.mobileBcom;
    }
    return AI_WATERMARK_CONFIG.mobileMcom;
  }
  if (isBcom) {
    return isHomeCategory ? AI_WATERMARK_CONFIG.square : AI_WATERMARK_CONFIG.bcomDefault;
  }
  return undefined;
});

const { brandName, productName, productExclusiveName, pdfEmailDescription } = useCommonProductGetters();
const { productName: consolidatedName, productExclusiveName: consolidatedExclusiveName } = useConsolidatedProductDetails();

const { selectedColor } = useTraits();

// TODO - move this to a shared resource later; keeping it here for simplicity for now
const altTextTitle = computed(() => {
  const _productName = consolidatedName.value || toValue(productName);
  if (isMcom) {
    const _productExclusiveName = consolidatedExclusiveName.value || toValue(productExclusiveName);
    return isExclusiveBrandFlag && _productExclusiveName ? _productExclusiveName : _productName;
  }
  return isChanelFlag ? _productName : toValue(pdfEmailDescription);
});
const imageAlt = computed(() => {
  const unwrappedAltTextTitle = altTextTitle.value || '';
  const unwrappedBrandName = toValue(brandName);
  const colorName = selectedColor.value?.name;
  const base = unwrappedAltTextTitle ? `${unwrappedBrandName} - ${unwrappedAltTextTitle}` : unwrappedBrandName;
  const aiDisclosure = 'Note: This image is AI-generated.';

  if (colorName && colorName !== 'No Color') {
    const withColorName = `${base} ${colorName}`;
    return isAiGeneratedImages.value ? `${withColorName} ${aiDisclosure}` : withColorName;
  }
  return isAiGeneratedImages.value ? `${base} ${aiDisclosure}` : base;
});

// Needs to be removed as on mobile there is no toplvl zoom
const topLevelZoomerMobile = computed(() => isTopLevelZoomerClick && isMobile.value);

onMounted(() => {
  if (isImageIndicatorExpEnabled) {
    once(useMarxentProvide());
  }

  if (props.showShopSimilar) start();
});

onKeyStroke(['ArrowLeft'], () => {
  if (focused.value) {
    slideShow.value.move(-1);
    triggerAltImageClickOnce();
  }
});
onKeyStroke(['ArrowRight'], () => {
  if (focused.value) {
    slideShow.value.move(1);
    triggerAltImageClickOnce();
  }
});

const MarxentComponent = markRaw(defineAsyncComponent(() => import('~/modules/marxent/components/MarxentQrBody.vue')));
const VTOComponent = markRaw(
  defineAsyncComponent(() => import('@nuxt-common/virtual-try-on-experience/app/components/VirtualTryOnExperienceInline.vue')),
);

const displayImages = computed(() => {
  const images = [...props.carouselImages];
  const target = indicatorIndex.value;

  if ((isMarxentEligible.value && images[target]) || vtoEligibleExperiment.value) {
    images[target] = {
      ...images[target],
      isARIndicatorExp: true,
      indicatorIndex: target,
      component: vtoEligibleExperiment.value ? VTOComponent : MarxentComponent,
      props: {
        productId: isShopTheSetExpEnabled ? selectedMember.value : productId,
        isARIndicatorSelected: () => isARIndicatorSelected.value,
        'onUpdate:allowSwipe': (value) => {
          allowSwipe.value = value;
        },
      },
    };
  }

  return images;
});

const allImages = computed(() => {
  return isMarxentEligible.value || vtoEligibleExperiment.value ? displayImages.value : props.carouselImages;
});

/**
 * @param {Image[]} a
 * @param {Image[]} b
 */
const imagesAreEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.findIndex((img, i) => img.filePath !== b[i].filePath) === -1;
};

const openOverlay = (event) => {
  emit('imageClick', event);
};

watch(
  [() => props.currentPage, () => props.currentPageOnHover, () => props.carouselImages, () => props.firstAltDot],
  ([currentPage, currentPageOnHover, carouselImages, firstAltDot], oldProps) => {
    if (currentPageOnHover !== null && slideShow.value) slideShow.value?.change(currentPageOnHover + 1);
    else if ((!isMobile.value || (props.inOverlay && isMobile.value)) && slideShow.value) {
      slideShow.value?.change(currentPage);
      emit('updateCarousel', currentPage);
      emit('updateFirstAltDot', firstAltDot);
    }

    if (!imagesAreEqual(carouselImages, oldProps[2])) {
      emit('updateCarousel', 1);
    }
  },
);

const handleVTOTouch = (e) => {
  if (!allowSwipe.value) {
    e.stopPropagation();
  }
};

/**
 * @param {number} index
 */
const getFetchPriority = (index) => {
  const isFirstOrSecond = index === 0 || index === 1;
  return isMobile.value ? isFirstOrSecond : isFirstOrSecond || index === props.carouselImages.length - 1;
};

/**
 * @param {number} index
 */
const slideShowChangeHandler = (index) => {
  if (props.currentPageOnHover === null) emit('updateCarousel', index);
  pauseVideo();
};
/**
 * @param {number} value
 */
const updateFirstAltDotHandler = (value) => {
  emit('updateFirstAltDot', value);
};
</script>

<style lang="scss" scoped>
@import '~@csgIcons/new/full-screen-dark-medium';
.product-image-wrapper {
  position: relative;
  cursor: zoom-in;
  height: 100%;

  &.mobile {
    @include breakpoint(small only) {
      height: auto;
    }
  }

  .image-layer {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    user-select: none;
    -webkit-user-drag: none;
    display: none;

    @include breakpoint(large) {
      display: block;
    }
    img {
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -o-user-select: none;
      user-select: none;
    }
  }
}

.product-image-wrapper-top-level {
  position: relative;
  height: 100%;
  .image-layer {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    user-select: none;
    -webkit-user-drag: none;
    display: none;
    img {
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -o-user-select: none;
      user-select: none;
    }
  }
}

.product-video-wrapper {
  height: 100%;
}
.focus-border {
  position: absolute;
  width: 100%;
  height: 100%;
  transition: 0.2s ease;
  opacity: 0;
  border: 2px solid rgb(0, 95, 204);
  z-index: 1;
  visibility: hidden;
  pointer-events: none;
}
.slideshow:focus-visible {
  .focus-border {
    @include breakpoint(medium up) {
      visibility: visible;
      opacity: 1;
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.top-level-zoom.zoomed-image {
  cursor: zoom-out;
}

.top-level-zoom {
  cursor: zoom-in;

  .video-container {
    cursor: auto;
  }
}

.zoomer-top-level-container {
  .main-image-zoom-background {
    touch-action: none;
  }

  .zoomed-in {
    .v-slideshow {
      touch-action: none;
    }

    img {
      touch-action: none;
    }
  }
}
.overlay-entry {
  position: absolute;
  cursor: pointer;
  bottom: 16px;
  left: 16px;
}
</style>

<style lang="scss" brand="mcom">
.ar-indicator-main-image-wrapper {
  position: relative;
  align-items: flex-end;
  height: 100%;
  box-sizing: border-box;
}
.ar-indicator-wrapper {
  height: 100%;
  cursor: initial;
}
</style>
